import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Instagram,
  Linkedin,
  Mail,
  MessageCircle,
  Phone,
  Send,
  Smartphone,
  Youtube,
} from 'lucide-react';

const CONTACT_PHONE_DISPLAY = '۰۲۱-۹۱۶۹۰۱۱۲';
const CONTACT_PHONE_TEL = '+982191690112';

const primaryNavigationItems = [
  { label: 'خانه', path: '/' },
  { label: 'دوره‌ها', path: '/courses' },
  { label: 'پادکست‌ها', path: '/podcasts' },
  { label: 'مقالات', path: '/articles' },
  { label: 'درباره ما', path: '/about' },
  { label: 'تماس با ما', path: '/contact' },
];

const quickAccessItems = [
  { label: 'سوالات متداول', path: '/faq' },
  { label: 'قوانین و مقررات', path: '/terms' },
  { label: 'حریم خصوصی', path: '/privacy' },
  { label: 'راهنمای خرید', path: '/guide' },
  { label: 'فرصت‌های شغلی', path: '/careers' },
  { label: 'لینک همکاری', path: '/partnership' },
];

const socialLinks = [
  { label: 'Instagram', href: '#', icon: Instagram },
  { label: 'Telegram', href: '#', icon: Send },
  { label: 'YouTube', href: '#', icon: Youtube },
  { label: 'LinkedIn', href: '#', icon: Linkedin },
  { label: 'WhatsApp', href: '#', icon: MessageCircle },
];

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <footer
      dir="rtl"
      className="mt-auto overflow-x-clip border-t border-slate-200/80 bg-gradient-to-b from-[#f8fafc] via-[#eef2ff]/70 to-[#f8fafc]"
    >
      <div className="relative mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute -right-1.5 -top-1.5 hidden h-72 w-72 rounded-2xl bg-[#7c3aed] lg:block" />
        <div className="relative z-10 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_18px_34px_-26px_rgba(15,23,42,0.3)] sm:p-5">
          <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr_1fr_1.1fr]">
            <div className="space-y-3 text-center lg:text-right">
              <button
                onClick={() => navigate('/')}
                className="mx-auto inline-flex w-fit items-center rounded-xl border border-slate-200 bg-gradient-to-b from-[#f8fafc] via-[#eef2ff]/70 to-[#f8fafc] px-3 py-2.5 transition hover:opacity-90 lg:mx-0"
              >
                <img src="/assets/logo-mane-haghighi-asli-1 (1).png" alt="لوگو حقیقی" className="h-24 w-auto object-contain" />
              </button>
              <p className="text-xs leading-6 text-slate-600">
                شروع مسیر رشد با آموزش‌های حرفه‌ای و محتوای کاربردی برای توسعه فردی و مالی.
              </p>
            </div>

            <div className="text-right">
              <p className="mb-2 text-sm font-black text-slate-800">لینک های مفید</p>
              <nav className="grid gap-1.5">
                {quickAccessItems.map((item) => {
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`group flex items-center justify-between rounded-xl px-2.5 py-2 text-right text-xs transition ${
                        location.pathname === item.path
                          ? 'bg-[#eef2ff] font-bold text-[#1C3B9B]'
                          : 'bg-white text-slate-600 hover:bg-[#f8faff] hover:text-[#1C3B9B]'
                      }`}
                    >
                      <span>{item.label}</span>
                      <ArrowLeft className="h-3.5 w-3.5 text-slate-400 transition group-hover:text-[#1C3B9B]" />
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="text-right">
              <p className="mb-2 text-sm font-black text-slate-800">دوره های پیشنهادی</p>
              <nav className="grid gap-1.5">
                {primaryNavigationItems.slice(0, 4).map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`group flex items-center justify-between rounded-xl px-2.5 py-2 text-right text-xs transition ${
                      location.pathname === item.path
                        ? 'bg-[#eef2ff] font-bold text-[#1C3B9B]'
                        : 'bg-white text-slate-600 hover:bg-[#f8faff] hover:text-[#1C3B9B]'
                    }`}
                  >
                    <span>{item.label}</span>
                    <ArrowLeft className="h-3.5 w-3.5 text-slate-400 transition group-hover:text-[#1C3B9B]" />
                  </button>
                ))}
              </nav>
            </div>

            <div className="space-y-3 text-right">
              <p className="text-sm font-black text-slate-800">ارتباط با ما</p>
              <a
                href={`tel:${CONTACT_PHONE_TEL}`}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:border-[#1C3B9B]/35 hover:text-[#1C3B9B]"
              >
                <Phone className="h-3.5 w-3.5" strokeWidth={2.2} />
                <span>{CONTACT_PHONE_DISPLAY}</span>
              </a>
              <form className="flex gap-1.5" onSubmit={(event) => event.preventDefault()}>
                <div className="relative min-w-0 flex-1">
                  <Mail className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    placeholder="ایمیل شما"
                    className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-2 pr-8 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#1C3B9B]/45 focus:bg-white focus:ring-2 focus:ring-[#1C3B9B]/10"
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-lg bg-gradient-to-l from-[#1C3B9B] to-[#C11277] px-2.5 text-xs font-bold text-white transition hover:opacity-95"
                >
                  عضویت
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
              </form>
              <div className="flex items-center gap-1.5">
                {socialLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-white to-[#eef2ff] text-slate-500 shadow-[0_10px_22px_-16px_rgba(15,23,42,0.45)] ring-1 ring-slate-200/80 transition hover:-translate-y-0.5 hover:from-[#1C3B9B] hover:to-[#7c3aed] hover:text-white hover:shadow-[0_14px_24px_-14px_rgba(28,59,155,0.55)]"
                      aria-label={item.label}
                    >
                      <Icon className="h-4 w-4 transition group-hover:scale-110" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-[#1C3B9B]/15 bg-gradient-to-r from-[#e0e7ff] via-[#f8fafc] to-[#fdf2ff] px-3 py-3 shadow-[0_14px_28px_-22px_rgba(28,59,155,0.45)]">
            <div className="flex flex-col items-center justify-between gap-3 text-center sm:flex-row sm:text-right">
              <div className="flex w-full flex-col items-center justify-between gap-2 sm:w-auto sm:flex-row">
                <p className="bg-gradient-to-l from-[#1C3B9B] to-[#C11277] bg-clip-text text-xs font-black text-transparent">
                  دریافت اپلیکیشن من حقیقی
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-l from-[#1C3B9B] to-[#7c3aed] px-3 py-1.5 text-[11px] font-bold text-white shadow-[0_10px_18px_-12px_rgba(28,59,155,0.75)] transition hover:opacity-95"
                  >
                    <Smartphone className="h-3.5 w-3.5" />
                    مایکت
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-700 shadow-[0_8px_15px_-12px_rgba(15,23,42,0.35)] transition hover:border-[#1C3B9B]/35 hover:text-[#1C3B9B]"
                  >
                    <Download className="h-3.5 w-3.5" />
                    بازار
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#0f172a] px-3 py-1.5 text-[11px] font-bold text-white shadow-[0_10px_18px_-12px_rgba(15,23,42,0.65)] transition hover:bg-[#1e293b]"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    دانلود مستقیم
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex flex-col items-center justify-between gap-2 text-center sm:flex-row sm:text-right">
              <p className="text-xs text-slate-600">© {new Date().getFullYear()} من حقیقی. تمامی حقوق محفوظ است.</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">نسخه ۱.۸.۱</span>
                {/* اینماد: طبق راهنمای رسمی enamad نباید rel="noopener noreferrer" روی این
                    لینک باشد، وگرنه لوگو نمایش داده نمی‌شود. مرورگرهای مدرن برای
                    target="_blank" به‌صورت پیش‌فرض noopener اعمال می‌کنند، پس حذفش امن است. */}
                <a
                  referrerPolicy="origin"
                  target="_blank"
                  href="https://trustseal.enamad.ir/?id=289322&Code=mnYVFVE9oOFZ1OKLX5Zl"
                  className="rounded-md border border-slate-200 bg-white p-1 transition hover:opacity-90"
                >
                  <img
                    referrerPolicy="origin"
                    src="https://trustseal.enamad.ir/logo.aspx?id=289322&Code=mnYVFVE9oOFZ1OKLX5Zl"
                    alt="نماد اعتماد الکترونیکی"
                    {...({ code: 'mnYVFVE9oOFZ1OKLX5Zl' } as Record<string, string>)}
                    className="h-16 w-auto cursor-pointer"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

